import { type FormEvent, useState } from "react"
import { ArrowLeftIcon, Clock3Icon, SearchIcon } from "lucide-react"

import { Button } from "@/components/ui/shadcn/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/shadcn/dialog"
import { Input } from "@/components/ui/shadcn/input"

type MobileSearchDialogProps = {
  open: boolean
  recentSearches: string[]
  onOpenChange: (open: boolean) => void
  onSubmit: (query: string) => void
}

const MobileSearchDialog = ({ open, recentSearches, onOpenChange, onSubmit }: MobileSearchDialogProps) => {
  const [query, setQuery] = useState("")

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    const searchQuery = query.trim()
    if (!searchQuery) return

    onSubmit(searchQuery)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        showCloseButton={false}
        variant="fullscreen"
        className="flex flex-col gap-5"
      >
        <DialogHeader className="sr-only">
          <DialogTitle>Поиск товаров</DialogTitle>
          <DialogDescription>Введите название запчасти, артикул или марку</DialogDescription>
        </DialogHeader>

        <form role="search" className="flex items-center gap-2" onSubmit={handleSubmit}>
          <Button type="button" variant="ghost" size="icon" aria-label="Закрыть поиск" onClick={() => onOpenChange(false)}>
            <ArrowLeftIcon />
          </Button>
          <Input
            autoFocus
            type="search"
            value={query}
            aria-label="Поиск товаров"
            placeholder="Название, артикул или марка"
            className="min-w-0 flex-1"
            onChange={(event) => setQuery(event.target.value)}
          />
          <Button type="submit" size="icon-lg" aria-label="Найти" disabled={!query.trim()}>
            <SearchIcon />
          </Button>
        </form>

        {recentSearches.length > 0 ? (
          <section aria-labelledby="mobile-recent-searches-title" className="min-h-0 flex-1 overflow-y-auto">
            <h2 id="mobile-recent-searches-title" className="mb-2 text-sm font-[550] text-muted-foreground">Недавние запросы</h2>
            <div className="flex flex-col gap-1">
              {recentSearches.map((recentQuery) => (
                <Button key={recentQuery.toLocaleLowerCase("ru-RU")} type="button" variant="ghost" className="justify-start" onClick={() => onSubmit(recentQuery)}>
                  <Clock3Icon data-icon="inline-start" />
                  <span className="truncate">{recentQuery}</span>
                </Button>
              ))}
            </div>
          </section>
        ) : (
          <div className="flex min-h-0 flex-1 flex-col justify-center gap-2 text-center">
            <p className="text-base font-[550] text-foreground">Найдём нужную запчасть</p>
            <p className="text-sm text-muted-foreground">Введите точное название или артикул и нажмите «Найти»</p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}

export default MobileSearchDialog
